import { Controller } from "@hotwired/stimulus"
import { template, escape } from "lodash"

// kontroler formularza cennika biura - dynamiczne wiersze, overflow, live preview
export default class extends Controller {
  static targets = [
    "tiersContainer", "tierTemplate", "tierRow", "addTierButton",
    "extrasContainer", "extraTemplate", "extraRow", "addExtraButton",
    "overflowRadio", "overflowPriceField", "overflowPrice", "overflowPriceLabel", "individualHint",
    "productName", "vatRate",
    "preview", "previewBody", "previewMainName", "previewMainVat", "previewMainPrice",
    "previewTotal", "previewDocsCount", "previewRowTemplate"
  ]

  static values = {
    maxTiers: { type: Number, default: 10 },
    maxExtras: { type: Number, default: 10 },
    additionalDocumentsLabel: { type: String, default: "" },
    documentsLabel: { type: String, default: "" },
    upToLabel: { type: String, default: "" },
    priceLabelText: { type: String, default: "" },
    perDocPriceLabelText: { type: String, default: "" }
  }

  connect() {
    this.tierIndex = Date.now()
    this.extraIndex = Date.now() + 1000
    this.tierRowTemplate = template(this.tierTemplateTarget.innerHTML)
    this.extraRowTemplate = template(this.extraTemplateTarget.innerHTML)
    this.previewRowTemplate = template(this.previewRowTemplateTarget.innerHTML)
    this.updatePreview()
    this._updateAddButtons()
  }

  addTier() {
    const visibleRows = this.tierRowTargets.filter(r => !r.classList.contains("hidden"))
    if (visibleRows.length >= this.maxTiersValue) return

    const container = document.createElement("div")
    container.innerHTML = this.tierRowTemplate({ INDEX: this.tierIndex })
    const row = container.firstElementChild

    // auto-wypełnienie range_from na podstawie ostatniego wiersza
    const lastRow = this.tierRowTargets[this.tierRowTargets.length - 1]
    if (lastRow) {
      const lastTo = lastRow.querySelector("input[name*='[range_to]']")
      if (lastTo?.value) {
        row.querySelector("input[name*='[range_from]']").value = parseInt(lastTo.value) + 1
      }
    }

    this.tiersContainerTarget.appendChild(row)
    this.tierIndex++
    this.updatePreview()
    this._updateAddButtons()
  }

  removeTier(event) {
    // nie usuwaj ostatniego przedziału
    const visibleRows = this.tierRowTargets.filter(r => !r.classList.contains("hidden"))
    if (visibleRows.length <= 1) return

    const row = event.currentTarget.closest(".tier-row")
    const destroyField = row.querySelector("[data-destroy-field]")

    if (destroyField) {
      // persisted record — ukryj wiersz i ustaw _destroy
      destroyField.value = "true"
      row.classList.add("hidden")
    } else {
      // nowy wiersz (dodany JS) — usuń z DOM
      row.remove()
    }
    this.updatePreview()
    this._updateAddButtons()
  }

  addExtra() {
    const visibleRows = this.extraRowTargets.filter(r => !r.classList.contains("hidden"))
    if (visibleRows.length >= this.maxExtrasValue) return

    const container = document.createElement("div")
    container.innerHTML = this.extraRowTemplate({ INDEX: this.extraIndex })
    const row = container.firstElementChild

    this.extrasContainerTarget.appendChild(row)
    this.extraIndex++
    this.updatePreview()
    this._updateAddButtons()
  }

  removeExtra(event) {
    const row = event.currentTarget.closest(".extra-row")
    const destroyField = row.querySelector("[data-destroy-field]")

    if (destroyField) {
      // persisted record — ukryj wiersz i ustaw _destroy
      destroyField.value = "true"
      row.classList.add("hidden")
    } else {
      // nowy wiersz (dodany JS) — usuń z DOM
      row.remove()
    }
    this.updatePreview()
    this._updateAddButtons()
  }

  updateOverflow() {
    const selected = this.overflowRadioTargets.find(r => r.checked)?.value

    // toggle pola cenowego overflow - sterowanie przez JS, bo pole jest poza radio buttonami
    if (this.hasOverflowPriceFieldTarget) {
      this.overflowPriceFieldTarget.classList.toggle("hidden", selected !== "fixed" && selected !== "per_doc")
    }

    // toggle hintu indywidualnej wyceny
    if (this.hasIndividualHintTarget) {
      this.individualHintTarget.classList.toggle("hidden", selected !== "individual")
    }

    // aktualizacja etykiety pola kwoty
    if (this.hasOverflowPriceLabelTarget && selected) {
      const label = selected === "per_doc" ? this.perDocPriceLabelTextValue : this.priceLabelTextValue
      if (label) this.overflowPriceLabelTarget.textContent = label
    }

    this.updatePreview()
  }

  updatePreview() {
    const PREVIEW_DOCS = 22
    const tiers = this._parseTiers()
    const extras = this._parseExtras()
    const productName = this.hasProductNameTarget ? this.productNameTarget.value : ""
    const vatRate = this.hasVatRateTarget ? this.vatRateTarget.value : "23"

    // dopasowanie przedziału
    let mainPrice = 0
    let mainDocsLabel = PREVIEW_DOCS
    let mainLabelPrefix = ""
    let overflowLine = null
    const matchedTier = tiers.find(t => PREVIEW_DOCS >= t.from && PREVIEW_DOCS <= t.to)

    if (matchedTier) {
      mainPrice = matchedTier.price
      mainDocsLabel = matchedTier.to
      mainLabelPrefix = this.upToLabelValue
    } else if (tiers.length > 0) {
      const lastTier = tiers[tiers.length - 1]
      if (PREVIEW_DOCS > lastTier.to) {
        mainPrice = lastTier.price
        mainDocsLabel = lastTier.to
        mainLabelPrefix = this.upToLabelValue
        const overflowType = this.overflowRadioTargets.find(r => r.checked)?.value
        const overflowPrice = this.hasOverflowPriceTarget ? parseFloat(this.overflowPriceTarget.value) || 0 : 0
        const overflowDocs = PREVIEW_DOCS - lastTier.to

        if (overflowType === "fixed") {
          overflowLine = { name: `${this.additionalDocumentsLabelValue} (${overflowDocs})`, price: overflowPrice }
        } else if (overflowType === "per_doc") {
          overflowLine = { name: `${this.additionalDocumentsLabelValue} (${overflowDocs})`, price: overflowDocs * overflowPrice }
        }
      }
    }

    // aktualizacja nazwy z liczbą dokumentów i VAT
    const displayName = productName ? `${productName} (${mainLabelPrefix}${mainDocsLabel} ${this.documentsLabelValue})` : "-"
    if (this.hasPreviewMainNameTarget) this.previewMainNameTarget.textContent = displayName
    if (this.hasPreviewMainVatTarget) this.previewMainVatTarget.textContent = vatRate === "zw" ? "zw." : vatRate + "%"
    if (this.hasPreviewMainPriceTarget) this.previewMainPriceTarget.textContent = this._formatCurrency(mainPrice)

    // dodatkowe wiersze w podglądzie
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.querySelectorAll(".preview-dynamic-row").forEach(r => r.remove())

      const vatDisplay = vatRate === "zw" ? "zw." : vatRate + "%"
      let rowNum = 2

      // wiersz overflow (dodatkowe dokumenty)
      if (overflowLine) {
        this.previewBodyTarget.insertAdjacentHTML("beforeend", this.previewRowTemplate({
          num: rowNum,
          name: escape(overflowLine.name),
          vatDisplay,
          price: this._formatCurrency(overflowLine.price)
        }))
        rowNum++
      }

      // wiersze pozycji dodatkowych
      extras.forEach((e) => {
        if (!e.name && e.price === 0) return
        const eVatDisplay = e.vatRate === "zw" ? "zw." : e.vatRate + "%"
        this.previewBodyTarget.insertAdjacentHTML("beforeend", this.previewRowTemplate({
          num: rowNum,
          name: escape(e.name || "-"),
          vatDisplay: eVatDisplay,
          price: this._formatCurrency(e.price)
        }))
        rowNum++
      })
    }

    // suma = główna + overflow + extras
    const overflowTotal = overflowLine ? overflowLine.price : 0
    const extrasTotal = extras.reduce((sum, e) => sum + e.price, 0)
    const total = mainPrice + overflowTotal + extrasTotal

    if (this.hasPreviewTotalTarget) this.previewTotalTarget.textContent = this._formatCurrency(total)
  }

  _parseTiers() {
    return this.tierRowTargets.filter(r => !r.classList.contains("hidden")).map(row => {
      const fromVal = row.querySelector("input[name*='[range_from]']")?.value
      const toVal = row.querySelector("input[name*='[range_to]']")?.value
      const priceVal = row.querySelector("input[name*='[price]']")?.value
      if (!fromVal || !toVal || !priceVal) return null
      return { from: parseInt(fromVal), to: parseInt(toVal), price: parseFloat(priceVal) }
    }).filter(t => t && !isNaN(t.from) && !isNaN(t.to) && !isNaN(t.price)).sort((a, b) => a.from - b.from)
  }

  _parseExtras() {
    return this.extraRowTargets.filter(r => !r.classList.contains("hidden")).map(row => {
      const name = row.querySelector("input[name*='[name]']")?.value || ""
      const price = parseFloat(row.querySelector("input[name*='[price]']")?.value) || 0
      const vatRate = row.querySelector("select[name*='[vat_rate]']")?.value || "23"
      return { name, price, vatRate }
    })
  }

  _formatCurrency(value) {
    return value.toFixed(2).replace(".", ",") + " PLN"
  }

  _updateAddButtons() {
    if (this.hasAddTierButtonTarget) {
      const tierCount = this.tierRowTargets.filter(r => !r.classList.contains("hidden")).length
      this.addTierButtonTarget.disabled = tierCount >= this.maxTiersValue
    }
    if (this.hasAddExtraButtonTarget) {
      const extraCount = this.extraRowTargets.filter(r => !r.classList.contains("hidden")).length
      this.addExtraButtonTarget.disabled = extraCount >= this.maxExtrasValue
    }
  }
}
